import { IChangePropertyPayload } from '@dash/editForm';
import { t } from 'src/i18n';
import { capitalize, isEmpty, isNumber } from 'lodash';
import { IDistrict } from '@dash/district';
import { isDateInFutureOrToday } from 'src/helpers/dateTime';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IUpdateAgencyContract,
  IFieldTypes,
  IUpdateFormInfoPayload,
  IChangeStepPayload,
  IChangeMarkupPayload,
  IChangePositionPricingPayload,
  IChangePositionEnabledPayload,
  IUpdateAgencyPayload,
  ICreateContractError,
  IStep,
  IChangeDistrictMarkupPayload,
  IChangePositionMarkupPayload,
  Errors,
  IApplyMarkupToPositionsPayload,
} from './types';
import { createReducer } from '../../../helpers/reducer';

function validate(attr: Attribute, value: IFieldTypes, step: IStep) {
  let errorMessage = '';

  const requiredAttributes: Attribute[] = ['starts_on', 'agency_name', 'operating_states', 'districts'];

  if (requiredAttributes.includes(attr)) {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t(`dash.agencies.update_contract.form_fields.${attr}_label`),
      });
    }
  }

  if (attr === 'starts_on' && value && typeof value === 'string' && !isDateInFutureOrToday(value)) {
    errorMessage = t('dash.validation.date_need_to_be_in_future_or_today_error');
  }

  if (step === 2) {
    if ((attr === 'markup_percentage' && !value) || (isNumber(value) && value <= 0)) {
      errorMessage = t('dash.validation.date_need_to_be_in_future_error');
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value?: any) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || '', 1),
});

const initialEditForm = {
  starts_on: getInitialField('starts_on'),
  agency_name: getInitialField('agency_name'),
  operating_states: getInitialField('operating_states', []),
  districts: getInitialField('districts', []),
  markup_percentage: getInitialField('markup_percentage'),
  apply_immediately: getInitialField('apply_immediately', true),
  legal_entity_id: getInitialField('legal_entity_id', null),
};

const initState: IUpdateAgencyContract = {
  isLoading: false,
  editForm: initialEditForm,
  formInfo: undefined,
  step: 1,
  errors: undefined,
  agencyByType: undefined,
  editedPositionsMarkup: [],
  originalPositionMarkups: {},
};

function onProcessActionRequest(state: IUpdateAgencyContract) {
  return {
    ...state,
    isLoading: true,
  };
}

function onProcessActionSuccess(state: IUpdateAgencyContract) {
  return {
    ...state,
    isLoading: true, // Keep loading true during redirect
    editForm: initialEditForm,
    step: 1,
  };
}

function onProcessActionError(state: IUpdateAgencyContract) {
  return {
    ...state,
    isLoading: false,
  };
}

function onUpdateFormInfo(state: IUpdateAgencyContract, { payload }: { payload: IUpdateFormInfoPayload }) {
  return {
    ...state,
    isLoading: false,
    formInfo: payload.data,
  };
}

function onChangeProperty(
  state: IUpdateAgencyContract,
  { payload }: { payload: IChangePropertyPayload<Attribute, IFieldTypes> },
): IUpdateAgencyContract {
  const { attr, value } = payload;
  const { editForm, step } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val, step),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onChangeStep(state: IUpdateAgencyContract, { payload }: { payload: IChangeStepPayload }) {
  return {
    ...state,
    step: payload.step,
  };
}

function getDistrictsWithUpdatedPricing(
  districts: IDistrict[],
  value: number,
  districtId: string,
  positionId?: string,
  attr: 'pay' | 'bill' = 'pay',
) {
  const markup = value ? value / 100 + 1 : 1;

  districts.forEach(district => {
    const districtMarkup = district.markup_percentage;

    if (districtId === district.id) {
      district.positions.forEach(position => {
        const positionMarkup = position.markup_percentage;

        if (attr === 'bill') {
          position.rates.forEach(rate => {
            rate.bill = parseFloat(rate.bill).toFixed(2);
          });
        } else if ((positionId && position.id === positionId) || (!positionId && districtMarkup === positionMarkup)) {
          position.rates.forEach(rate => {
            rate.bill = (parseFloat(rate.pay) * markup).toFixed(2);
          });
        }
      });
    }
  });

  return districts;
}

function onChangeMarkup(state: IUpdateAgencyContract, { payload }: { payload: IChangeMarkupPayload }) {
  const { markup, district_id, position_id } = payload;
  const { editForm } = state;

  if (Number.isFinite(markup) && editForm.districts.value) {
    const updatedDistricts = getDistrictsWithUpdatedPricing(editForm.districts.value, markup, district_id, position_id);

    if (updatedDistricts) {
      editForm.districts.value = updatedDistricts;
    }

    return { ...state, editForm };
  }

  return { ...state };
}

function onChangePositionEnabled(
  state: IUpdateAgencyContract,
  { payload }: { payload: IChangePositionEnabledPayload },
) {
  const { district_id, position_id, enabled } = payload;
  const { editForm } = state;

  const districts = editForm.districts?.value || [];
  const district = districts.find(item => item.id === district_id);

  if (district) {
    const position = district.positions.find(item => item.id === position_id);

    if (position) {
      position.enabled = enabled;
      position.bookable = enabled;

      const markup = editForm.markup_percentage.value;

      if (markup) {
        const updatedDistricts = getDistrictsWithUpdatedPricing(districts, markup, district_id, position_id);

        if (updatedDistricts) {
          editForm.districts.value = updatedDistricts;
        }
      }

      return { ...state, editForm };
    }

    return { ...state };
  }

  return { ...state };
}

function onChangePositionPricing(
  state: IUpdateAgencyContract,
  { payload }: { payload: IChangePositionPricingPayload },
) {
  const { district_id, position_id, skill_id, value, attr } = payload;
  const { editForm } = state;

  const districts = editForm.districts?.value || [];
  const district = districts.find(item => item.id === district_id);

  if (district) {
    const position = district.positions.find(item => item.id === position_id);

    if (position) {
      const rate = position.rates.find(item => item.skill_id === skill_id);

      if (rate) {
        rate[attr] = value;

        const markup = position.markup_percentage;

        const updatedDistricts = getDistrictsWithUpdatedPricing(districts, markup, district_id, position_id, attr);

        editForm.districts.value = updatedDistricts;

        const errors = { ...state.errors };

        if (errors?.[district_id] && errors[district_id]?.positions?.[position_id]?.rates?.[skill_id]) {
          delete errors[district_id]?.positions?.[position_id]?.rates?.[skill_id];

          const rates = errors[district_id]?.positions?.[position_id]?.rates;

          const ratesErrors = Object.keys(rates || {});

          const hasValidationError = errors[district_id]?.positions?.[position_id]?.validation;

          if (!ratesErrors.length && !hasValidationError) {
            delete errors[district_id]?.positions?.[position_id];
          }

          const positions = errors[district_id]?.positions || {};

          const positionsErrors = Object.keys(positions);

          if (!positionsErrors.length) {
            delete errors[district_id];
          }
        }

        return { ...state, editForm, errors };
      }

      return { ...state };
    }

    return { ...state };
  }

  return { ...state };
}

function onUpdateAgency(state: IUpdateAgencyContract, { payload }: { payload: IUpdateAgencyPayload }) {
  return {
    ...state,
    isLoading: false,
    agencyByType: payload.agencyByType,
    step: 1,
  };
}

function onCreateContractError(state: IUpdateAgencyContract, { payload }: { payload: ICreateContractError }) {
  const { data, districtErrorMessage } = payload;

  const validationErrors = Object.entries(data || {});
  let step = 2;
  const editForm = { ...state.editForm };

  const baseErrorMessage: string[] = [];

  validationErrors.forEach(([attr, error]) => {
    if (attr === 'base') {
      // @ts-ignore
      baseErrorMessage.push(error);
    }

    // @ts-ignore
    if (editForm[attr] && attr !== 'districts') {
      const errorMessage =
        Array.isArray(error) && typeof error[0] === 'string' ? capitalize(error[0]) : 'Invalid error format';

      // @ts-ignore
      editForm[attr] = {
        // @ts-ignore
        value: editForm[attr].value,
        error: errorMessage,
        name: attr,
        touched: true,
      };
    }

    if (districtErrorMessage?.length > 1) {
      editForm.districts = {
        value: editForm.districts.value,
        error: districtErrorMessage,
        name: attr,
        touched: true,
      };

      step = 1;
    }
  });

  const districtsErrors: { [key: string]: {} } = {};

  validationErrors.forEach(([attr, error]) => {
    if (error && ['districts'].includes(attr)) {
      step = 2;
      Object.values(error).forEach(district => {
        const id = district.original_district_id;

        if (id) {
          districtsErrors[id] = district;
        }
      });
    }
  });

  validationErrors.forEach(([attr, error]) => {
    if (error && ['starts_on', 'name', 'email'].includes(attr)) {
      step = 1;
    }
  });

  return { ...state, step, editForm, errors: districtsErrors };
}

function onChangeDistrictMarkup(
  state: IUpdateAgencyContract,
  { payload }: { payload: IChangeDistrictMarkupPayload & { apply_to_all_positions?: boolean } },
) {
  const { editForm, editedPositionsMarkup } = state;
  const { district_id, markup_percentage, apply_to_all_positions } = payload;

  const districts = editForm.districts?.value || [];
  const district = districts.find(d => d.id === district_id);

  if (!district) return { ...state };

  const previousDistrictMarkup = district.markup_percentage;
  district.markup_percentage = markup_percentage;

  if (apply_to_all_positions) {
    district.positions.forEach(pos => {
      pos.markup_percentage = markup_percentage;

      const key = `${district_id}-${pos.id}`;
      const index = editedPositionsMarkup.indexOf(key);

      if (index !== -1) editedPositionsMarkup.splice(index, 1);
    });
  } else if (district.apply_markup_to_positions) {
    district.positions.forEach(pos => {
      const key = `${district_id}-${pos.id}`;
      const isUnedited =
        // @ts-ignore
        (!pos.markup_percentage || parseFloat(pos.markup_percentage) === parseFloat(previousDistrictMarkup)) &&
        !editedPositionsMarkup.includes(key);

      if (isUnedited) pos.markup_percentage = markup_percentage;
    });
  }

  return {
    ...state,
    editForm,
    editedPositionsMarkup,
  };
}

function onChangePositionMarkup(state: IUpdateAgencyContract, { payload }: { payload: IChangePositionMarkupPayload }) {
  const { editForm, editedPositionsMarkup } = state;
  const { district_id, position_id, markup_percentage } = payload;

  const districts = editForm.districts?.value || [];
  const district = districts.find(item => item.id === district_id);

  if (district) {
    district.positions.forEach(position => {
      if (position.id === position_id) {
        position.markup_percentage = markup_percentage;
      }
    });

    return {
      ...state,
      editForm,
      editedPositionsMarkup: [...editedPositionsMarkup, `${district_id}-${position_id}`],
    };
  }

  return {
    ...state,
  };
}

function onValidatePositionError(state: IUpdateAgencyContract, { payload }: { payload: { errors: Errors } }) {
  const existingErrors = state.errors || {};
  const newErrors = payload.errors || {};
  const mergedErrors = { ...existingErrors };

  Object.keys(newErrors).forEach(districtId => {
    if (!mergedErrors[districtId]) {
      mergedErrors[districtId] = newErrors[districtId];
    } else {
      const existingPositions = mergedErrors[districtId].positions || {};
      const newPositions = newErrors[districtId].positions || {};
      mergedErrors[districtId] = {
        ...mergedErrors[districtId],
        positions: { ...existingPositions, ...newPositions },
      };
    }
  });

  return {
    ...state,
    isLoading: false,
    errors: mergedErrors,
  };
}

function onValidatePositionSuccess(state: IUpdateAgencyContract) {
  return {
    ...state,
    isLoading: false,
  };
}

function onToggleApplyMarkupToPositions(
  state: IUpdateAgencyContract,
  { payload }: { payload: IApplyMarkupToPositionsPayload },
) {
  const { district_id, checked } = payload;
  const { editForm, originalPositionMarkups = {} } = state;

  const districts = editForm.districts?.value || [];
  const district = districts.find(d => d.id === district_id);

  if (!district) return { ...state };

  if (checked) {
    // save current markups before applying district value
    district.positions.forEach(pos => {
      originalPositionMarkups[`${district_id}-${pos.id}`] = pos.markup_percentage;
      pos.markup_percentage = district.markup_percentage;
    });
  } else {
    // revert to saved markups
    district.positions.forEach(pos => {
      const key = `${district_id}-${pos.id}`;

      if (originalPositionMarkups[key] !== undefined) {
        pos.markup_percentage = originalPositionMarkups[key];
        delete originalPositionMarkups[key];
      }
    });
  }

  district.apply_markup_to_positions = checked;

  return {
    ...state,
    editForm,
    originalPositionMarkups,
  };
}

function onClearState() {
  return {
    ...initState,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.PROCESS_ACTION_SUCCESS]: onProcessActionSuccess,
  [actionsTypes.PROCESS_ACTION_ERROR]: onProcessActionError,
  [actionsTypes.UPDATE_FORM_INFO]: onUpdateFormInfo,
  [actionsTypes.CHANGE_STEP]: onChangeStep,
  [actionsTypes.CHANGE_MARKUP]: onChangeMarkup,
  [actionsTypes.CHANGE_POSITION_PRICING]: onChangePositionPricing,
  [actionsTypes.CHANGE_POSITION_ENABLED]: onChangePositionEnabled,
  [actionsTypes.UPDATE_AGENCY]: onUpdateAgency,
  [actionsTypes.ON_CREATE_CONTRACT_ERROR]: onCreateContractError,
  [actionsTypes.CHANGE_DISTRICT_MARKUP]: onChangeDistrictMarkup,
  [actionsTypes.CHANGE_POSITION_MARKUP]: onChangePositionMarkup,
  [actionsTypes.ON_VALIDATE_POSITION_ERROR]: onValidatePositionError,
  [actionsTypes.ON_VALIDATE_POSITION_SUCCESS]: onValidatePositionSuccess,
  [actionsTypes.CLEAR_STATE]: onClearState,
  [actionsTypes.APPLY_MARKUP_TO_POSITIONS]: onToggleApplyMarkupToPositions,
});
